import React from 'react';
import { WhatsAppAvatar } from '@/components/whatsapp-inbox/WhatsAppAvatar';
import { MessageSquare, MessageCircleQuestion, AlertOctagon, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Card de notificação no estilo do WhatsApp:
 * - Variant 'whatsapp': visual sutil, paleta WhatsApp (verde) com detalhes
 *   em vermelho bordô. Header em verde claro tintado, barra lateral fina
 *   em verde WhatsApp, badge bordô.
 * - Variants 'duvida' e 'nota1' mantêm o visual urgente colorido.
 * - Mostra a foto do contato (vinda do Evolution API) quando disponível.
 *
 * Persistente — só some quando o usuário clica em Abrir, em Depois, ou abre a conversa.
 */

export type NotificationVariant = 'whatsapp' | 'duvida' | 'nota1';

// Paleta alinhada com o botão "Finalizar Conversa" do CRM (emerald-50/200/600/700)
const WA_GREEN = '#059669';      // emerald-600 (CTA principal)
const WA_GREEN_DEEP = '#047857'; // emerald-700 (texto/header)
const WA_GREEN_TINT = '#ECFDF5'; // emerald-50 (fundo header sutil)
const BORDO = '#7a1f2b';         // vermelho bordô (detalhes/badge)

const VARIANTS: Record<NotificationVariant, {
  bg: string; fg: string; ctaLabel: string; Icon: typeof MessageSquare;
}> = {
  whatsapp:         { bg: WA_GREEN,  fg: WA_GREEN_DEEP, ctaLabel: 'Abrir conversa',         Icon: MessageSquare },
  duvida:           { bg: '#9333ea', fg: '#ffffff',     ctaLabel: 'Atender dúvida agora',   Icon: MessageCircleQuestion },
  nota1:            { bg: '#dc2626', fg: '#ffffff',     ctaLabel: 'Atender cliente agora',  Icon: AlertOctagon },
};

export interface WhatsAppNotifCardProps {
  titulo: string;
  nome: string;
  preview: string;
  badge?: string;
  /** Foto do contato (vinda do Evolution API). */
  fotoUrl?: string | null;
  variant?: NotificationVariant;
  ctaLabel?: string;
  onAbrir: () => void;
  onClose: () => void;
}

export const WhatsAppNotificationCard: React.FC<WhatsAppNotifCardProps> = ({
  titulo,
  nome,
  preview,
  badge,
  fotoUrl,
  variant = 'whatsapp',
  ctaLabel,
  onAbrir,
  onClose,
}) => {
  const v = VARIANTS[variant];
  const Icon = v.Icon;
  const cta = ctaLabel ?? v.ctaLabel;
  const isWa = variant === 'whatsapp';

  // Estilo sutil só pra variant whatsapp; demais (duvida/nota1) seguem agressivo
  const headerStyle = isWa
    ? { background: WA_GREEN_TINT, color: WA_GREEN_DEEP }
    : { background: v.bg, color: v.fg };
  const ctaStyle = isWa
    ? { background: WA_GREEN, color: '#ffffff' }
    : { background: v.bg, color: v.fg };
  const badgeStyle = isWa
    ? { background: BORDO, color: '#ffffff' }
    : { background: 'rgba(255,255,255,0.3)', color: v.fg };

  return (
    <div
      className={cn(
        // pointer-events-auto: garante que o card capture o clique mesmo quando
        // há um painel/modal aberto (Radix Sheet põe pointer-events:none fora
        // dele). Sem isso, o clique em "Depois"/"Abrir conversa" atravessava o
        // card e caía nos botões do painel atrás (ex.: disparava o "Kanban" e o
        // erro "quadro do pedido sem itens"), e o card não fechava.
        'pointer-events-auto relative w-[340px] rounded-2xl overflow-hidden dark:bg-muted',
        'bg-card',
        isWa
          ? 'ring-1 ring-success/70 dark:ring-success/40'
          : 'shadow-2xl border border-black/10 dark:border-white/10',
      )}
      style={
        isWa
          ? { boxShadow: '0 10px 30px -12px rgba(5,150,105,0.25), 0 4px 10px -4px rgba(0,0,0,0.06)' }
          : undefined
      }
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5"
        style={headerStyle}
      >
        <Icon className="h-3.5 w-3.5 opacity-90" />
        <span
          className={cn(
            'flex-1 truncate',
            isWa
              ? 'text-[11px] font-semibold uppercase tracking-wide'
              : 'text-xs font-bold uppercase tracking-wide',
          )}
        >
          {titulo}
        </span>
        {badge && (
          <span
            className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded tracking-wide"
            style={badgeStyle}
          >
            {badge}
          </span>
        )}
        <button
          onClick={onClose}
          className={cn(
            'p-0.5 rounded transition',
            isWa ? 'hover:bg-success/70' : 'hover:bg-black/10',
          )}
          aria-label="Fechar"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Corpo */}
      <button
        type="button"
        onClick={onAbrir}
        className="w-full text-left flex items-center gap-3 px-3.5 py-3 hover:bg-accent/40 transition"
      >
        <div className="flex-shrink-0 rounded-full ring-2 ring-success/40 p-[1px] bg-card">
          <WhatsAppAvatar nome={nome} fotoUrl={fotoUrl} size="lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate leading-tight">
            {nome}
          </p>
          <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-snug">
            {preview}
          </p>
        </div>
      </button>

      {/* Footer */}
      <div
        className={cn(
          'flex items-center gap-2 px-3.5 py-2.5 border-t bg-card',
          isWa ? 'border-success/30' : 'border-border',
        )}
      >
        <button
          onClick={onClose}
          className="px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition"
        >
          Depois
        </button>
        <button
          onClick={onAbrir}
          className={cn(
            'ml-auto px-3.5 py-1.5 text-xs font-semibold rounded-lg transition',
            isWa ? 'shadow-sm hover:brightness-95' : 'shadow-sm hover:brightness-95 font-bold',
          )}
          style={ctaStyle}
        >
          {cta}
        </button>
      </div>
    </div>
  );
};

export default WhatsAppNotificationCard;
